import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Sparkles, ArrowLeft, Building2, Globe, Target, CheckCircle2 } from 'lucide-react'
import { organizationApi, CompanyAnalysisResponse } from '@/services/organization-api'
import { publicAnalysisApi, PublicAnalysisResponse } from '@/services/public-api'
import { useAuthStore } from '@/stores/auth-store'
import { useOrgStore } from '@/stores/org-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TagInput } from '@/components/ui/tag-input'
import { CountryRegionSelect } from '@/components/ui/country-region-select'
import { MultiSelect } from '@/components/ui/multi-select'
import { expandGeographicFocusToCountryCodes } from '@/constants/countries'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { TARGET_MARKET_OPTIONS } from '@/constants/target-markets'
import { isValidWebsiteInput, normalizeWebsiteUrl } from '@/lib/website-url'

// LocalStorage key for pending analysis from public demo
const PENDING_ANALYSIS_KEY = 'pending_analysis_id'

type SubStep = 'claiming' | 'company-info' | 'analyzing' | 'review'

const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  el: 'GR',
  gr: 'GR', // fallback when LLM returns "Greek" -> [:2] yields "gr"
  tr: 'TR',
  de: 'DE',
  nl: 'NL',
  pl: 'PL',
  cs: 'CZ',
  sk: 'SK',
  hu: 'HU',
  ro: 'RO',
  bg: 'BG',
  hr: 'HR',
  sl: 'SI',
  sr: 'RS',
  uk: 'UA',
  ja: 'JP',
  ko: 'KR',
  th: 'TH',
  vi: 'VN',
  id: 'ID',
  he: 'IL',
  fi: 'FI',
  sv: 'SE',
  no: 'NO',
  da: 'DK',
}

function normalizeCountryCodes(values: string[] | null | undefined): string[] {
  const seen = new Set<string>()
  const normalized: string[] = []

  for (const rawValue of values ?? []) {
    const value = rawValue.trim().toUpperCase()
    if (!/^[A-Z]{2}$/.test(value)) continue
    if (seen.has(value)) continue
    seen.add(value)
    normalized.push(value)
  }

  return normalized
}

function inferTargetCountriesFromWebsiteLanguage(
  websiteLanguage: string | null | undefined
): string[] {
  if (!websiteLanguage) return []

  const languageCode = websiteLanguage.trim().toLowerCase().slice(0, 2)
  if (!languageCode || languageCode === 'en') {
    return []
  }

  const country = LANGUAGE_TO_COUNTRY[languageCode]
  return country ? [country] : []
}

function normalizeIndustryTag(value: string): string {
  return value
    .replace(/_/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

function formatIndustryTags(values: string[]): string {
  const seen = new Set<string>()
  const normalized: string[] = []
  for (const value of values) {
    const tag = normalizeIndustryTag(value)
    if (!tag) continue
    const key = tag.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    normalized.push(tag)
  }
  return normalized.join(', ')
}

export default function CreateOrgStep() {
  const navigate = useNavigate()
  const fetchOrgs = useOrgStore((s) => s.fetchOrgs)
  const setOnboardingCompleted = useAuthStore((s) => s.setOnboardingCompleted)

  // Track if we've already tried to claim a pending analysis
  const claimAttemptedRef = useRef(false)

  // Sub-step state - starts with 'claiming' if we have a pending analysis
  const [subStep, setSubStep] = useState<SubStep>('claiming')

  // Step 1: Company info (website required)
  const [website, setWebsite] = useState('')

  // Step 2: Analysis results
  const [analysisResult, setAnalysisResult] = useState<CompanyAnalysisResponse | null>(null)
  const [analysisProgress, setAnalysisProgress] = useState(0)

  // Step 3: Review & customize
  const [reviewName, setReviewName] = useState('')
  const [industry, setIndustry] = useState('')
  const [targetMarkets, setTargetMarkets] = useState<string[]>([])
  const [prospectingGoals, setProspectingGoals] = useState('')
  const [targetCountries, setTargetCountries] = useState<string[]>([])

  // Track if we pre-populated from a claimed analysis
  const [claimedAnalysis, setClaimedAnalysis] = useState<PublicAnalysisResponse | null>(null)

  // UI state
  const [error, setError] = useState<string | null>(null)
  const [analysisWarning, setAnalysisWarning] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Validation for step 1 - website is required
  const websiteValid = isValidWebsiteInput(website)
  const canContinue = websiteValid

  // Track analysis phase for status messages
  const [analysisPhase, setAnalysisPhase] = useState<string>('starting')

  // Check for pending analysis on mount and try to claim it
  useEffect(() => {
    if (claimAttemptedRef.current) return
    claimAttemptedRef.current = true

    const pendingAnalysisId = localStorage.getItem(PENDING_ANALYSIS_KEY)

    if (!pendingAnalysisId) {
      // No pending analysis - go to normal flow
      setSubStep('company-info')
      return
    }

    // Try to claim the analysis
    const claimPendingAnalysis = async () => {
      try {
        const analysis = await publicAnalysisApi.claimAnalysis(pendingAnalysisId)

        // Clear the pending analysis key regardless of success
        localStorage.removeItem(PENDING_ANALYSIS_KEY)

        if (!analysis || analysis.status !== 'completed') {
          // Claim failed or analysis not completed - normal flow
          setSubStep('company-info')
          return
        }

        // Success! Pre-populate fields from the claimed analysis
        setClaimedAnalysis(analysis)

        // Pre-fill website
        if (analysis.website_url) {
          setWebsite(analysis.website_url)
        }

        // Pre-fill company name
        if (analysis.company_profile?.suggested_name) {
          setReviewName(analysis.company_profile.suggested_name)
        }

        // Pre-fill industry from business_category and industry_tags
        const industryParts: string[] = []
        if (analysis.company_profile?.business_category) {
          industryParts.push(analysis.company_profile.business_category)
        }
        if (analysis.company_profile?.industry_tags?.length) {
          industryParts.push(...analysis.company_profile.industry_tags)
        }
        if (industryParts.length > 0) {
          setIndustry(formatIndustryTags(industryParts))
        }

        // Pre-fill target markets
        if (analysis.company_profile?.target_markets?.length) {
          setTargetMarkets(analysis.company_profile.target_markets)
        }

        // Pre-fill prospecting goals
        if (analysis.company_profile?.prospecting_goals?.length) {
          setProspectingGoals(analysis.company_profile.prospecting_goals.join(', '))
        }

        // Pre-fill target countries: prefer language inference (more specific, e.g. Greek → Greece),
        // else use geographic_focus (supports region names like "europe" and country names)
        const inferredFromLanguage = inferTargetCountriesFromWebsiteLanguage(
          analysis.company_profile?.website_language
        )
        const fromGeo = expandGeographicFocusToCountryCodes(
          analysis.ideal_customer_profile?.geographic_focus
        )
        if (inferredFromLanguage.length > 0) {
          setTargetCountries(inferredFromLanguage)
        } else if (fromGeo.length > 0) {
          setTargetCountries(fromGeo)
        }

        // Skip directly to review step
        setSubStep('review')
      } catch {
        // Claim failed - continue with normal flow
        localStorage.removeItem(PENDING_ANALYSIS_KEY)
        setSubStep('company-info')
      }
    }

    claimPendingAnalysis()
  }, [])

  // Handle step 1 continue
  const handleContinue = async () => {
    setError(null)
    setAnalysisWarning(null)
    setAnalysisPhase('starting')
    setAnalysisProgress(0)
    setSubStep('analyzing')

    // Normalize website URL before sending
    const normalizedWebsite = normalizeWebsiteUrl(website)

    if (!normalizedWebsite) {
      setError('Please enter a valid website URL')
      setSubStep('company-info')
      return
    }

    try {
      // Start analysis
      const result = await organizationApi.analyzeCompany({
        website: normalizedWebsite,
      })

      setAnalysisProgress(100)
      setAnalysisPhase('done')
      setAnalysisResult(result)

      // Pre-fill fields from analysis
      if (result.suggested_name) {
        setReviewName(result.suggested_name)
      } else {
        setReviewName('')
      }

      if (result.suggested_industry && result.suggested_industry.length > 0) {
        setIndustry(formatIndustryTags(result.suggested_industry))
      }
      if (result.suggested_target_markets && result.suggested_target_markets.length > 0) {
        setTargetMarkets(result.suggested_target_markets)
      }
      if (result.suggested_prospecting_goals && result.suggested_prospecting_goals.length > 0) {
        setProspectingGoals(result.suggested_prospecting_goals.join(', '))
      }

      const inferredTargetCountriesFromResponse = normalizeCountryCodes(
        result.suggested_target_countries
      )
      const inferredTargetCountries =
        inferredTargetCountriesFromResponse.length > 0
          ? inferredTargetCountriesFromResponse
          : inferTargetCountriesFromWebsiteLanguage(result.website_language)
      if (inferredTargetCountries.length > 0) {
        setTargetCountries((prev) => (prev.length > 0 ? prev : inferredTargetCountries))
      }

      // Check if we got meaningful results
      if (result.confidence < 0.3) {
        setAnalysisWarning(
          'We couldn\'t extract much information automatically. Please review and fill in the details below.'
        )
      }

      // Short delay to show 100% progress
      setTimeout(() => {
        setSubStep('review')
      }, 600)
    } catch {
      // Analysis failed - still continue to review
      setAnalysisWarning(
        'Analysis couldn\'t be completed. Please fill in your company details manually.'
      )
      setSubStep('review')
    }
  }

  // Realistic progress simulation matching actual backend timings
  // Website crawl: ~25s homepage + ~6s about + ~4s×2 pages + ~5s LLM = ~45s
  useEffect(() => {
    if (subStep !== 'analyzing') return

    const startTime = Date.now()

    // Total expected duration: website ~45s
    const totalExpected = 45000

    // Phase transitions (percentage thresholds and labels)
    const phases = [
      { pct: 0, label: 'Connecting to website...' },
      { pct: 8, label: 'Loading homepage...' },
      { pct: 45, label: 'Reading additional pages...' },
      { pct: 70, label: 'Analyzing content...' },
      { pct: 85, label: 'Identifying industry and prospecting goals...' },
      { pct: 95, label: 'Finalizing...' },
    ]

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const fraction = Math.min(elapsed / totalExpected, 1)

      // Ease-out curve: fast start, gradual slowdown, never quite hits 95
      // y = 95 * (1 - e^(-3x)) gives a nice curve that reaches ~95% at x=1
      const target = 95 * (1 - Math.exp(-3 * fraction))

      // Add a tiny bit of jitter so it doesn't look robotic
      const jitter = (Math.random() - 0.5) * 1.5

      setAnalysisProgress((prev) => {
        const next = Math.min(target + jitter, 95)
        // Only move forward
        return Math.max(prev, next)
      })

      // Update phase label based on current progress
      const currentPct = target
      for (let i = phases.length - 1; i >= 0; i--) {
        if (currentPct >= phases[i].pct) {
          setAnalysisPhase(phases[i].label)
          break
        }
      }
    }, 800)

    return () => clearInterval(interval)
  }, [subStep])

  // Handle back button
  const handleBack = useCallback(() => {
    setSubStep('company-info')
    setAnalysisWarning(null)
  }, [])

  // Handle final submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate name is provided
    if (!reviewName.trim()) {
      setError('Organization name is required')
      return
    }

    setIsSubmitting(true)

    // Normalize website URL before sending
    const normalizedWebsite = normalizeWebsiteUrl(website)

    // Parse prospecting goals from comma-separated string to array
    const goalsArray = prospectingGoals
      .split(',')
      .map((g) => g.trim())
      .filter((g) => g.length > 0)

    // Resolve ICP data: prefer from onboarding analysis, fall back to claimed analysis
    const icpData = analysisResult?.ideal_customer_profile
      ?? (claimedAnalysis?.ideal_customer_profile
        ? {
            services_offered: claimedAnalysis.ideal_customer_profile.services_offered,
            target_customer_industries: claimedAnalysis.ideal_customer_profile.target_customer_industries,
            target_company_types: claimedAnalysis.ideal_customer_profile.target_company_types,
            target_company_size: claimedAnalysis.ideal_customer_profile.target_company_size,
            value_proposition: claimedAnalysis.ideal_customer_profile.value_proposition,
            buyer_persona: claimedAnalysis.ideal_customer_profile.buyer_persona,
            geographic_focus: claimedAnalysis.ideal_customer_profile.geographic_focus,
          }
        : undefined)
    const sampleListQuery = analysisResult?.suggested_list_query
      ?? claimedAnalysis?.company_profile?.suggested_list_query
      ?? undefined

    try {
      const org = await organizationApi.create({
        name: reviewName.trim(),
        website: normalizedWebsite || undefined,
        industry: industry.trim() || undefined,
        target_markets: targetMarkets,
        target_countries: targetCountries,
        prospecting_goals: goalsArray,
        sample_list_query: sampleListQuery,
        // Pass ICP data extracted during "Analyze Website" or from claimed analysis
        // This tells the backend to skip re-crawling the website
        ideal_customer_profile: icpData,
        // Pass claimed analysis ID for audit trail
        claimed_analysis_id: claimedAnalysis?.id,
      })

      await organizationApi.completeOnboarding(org.id)
      await fetchOrgs()
      setOnboardingCompleted(true)
      navigate('/app/dashboard')
    } catch {
      setError('Could not create organization. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Sub-step 0: Claiming - checking for pending analysis
  if (subStep === 'claiming') {
    return (
      <div className="space-y-6 py-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Loading your analysis...</h2>
          <p className="text-muted-foreground mt-2">
            Preparing your company data from the website analysis
          </p>
        </div>
      </div>
    )
  }

  // Sub-step 1: Company Info - Website Required
  if (subStep === 'company-info') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Enter your company website</h2>
          <p className="text-muted-foreground mt-1">
            We'll analyze your website to understand your business and suggest prospecting goals
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Website URL - Primary/Required Field */}
          <div className="space-y-2">
            <Label htmlFor="website" className="text-base font-medium">
              Website URL <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="website"
                type="text"
                placeholder="example.com or https://example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className={`pl-11 h-12 text-base ${website.trim() && !websiteValid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                autoFocus
              />
            </div>
            {website.trim() && !websiteValid ? (
              <p className="text-sm text-destructive">
                Please enter a valid website (e.g., example.com or https://example.com)
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                We'll analyze your website to understand your business
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button
            onClick={handleContinue}
            disabled={!canContinue}
            className="min-w-[160px]"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Analyze Website
          </Button>
        </div>
      </div>
    )
  }

  // Sub-step 2: Analyzing
  if (subStep === 'analyzing') {
    return (
      <div className="space-y-6 py-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Sparkles className="h-8 w-8 text-primary animate-pulse" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Analyzing your website...</h2>
          <p className="text-muted-foreground mt-2">
            Crawling your website and extracting business insights
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-3">
          <Progress value={analysisProgress} className="h-2" />
          <p className="text-center text-sm text-muted-foreground transition-opacity duration-300">
            {analysisPhase}
          </p>
          {analysisProgress < 50 && (
            <p className="text-center text-xs text-muted-foreground/70">
              This may take up to a minute depending on the website
            </p>
          )}
        </div>
      </div>
    )
  }

  // Sub-step 3: Review & Customize
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Review & customize</h2>
          <p className="text-muted-foreground">
            {claimedAnalysis
              ? 'Your website analysis is ready! Review the details below.'
              : analysisResult && analysisResult.confidence >= 0.5
                ? 'We\'ve filled in some details based on your website. Feel free to adjust.'
                : 'Complete your organization profile'}
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {analysisWarning && (
        <Alert>
          <AlertDescription>{analysisWarning}</AlertDescription>
        </Alert>
      )}

      {claimedAnalysis && (
        <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            We've pre-filled your company info from your earlier website analysis. Review and customize below.
          </AlertDescription>
        </Alert>
      )}

      {!claimedAnalysis && analysisResult && analysisResult.confidence >= 0.5 && (
        <Alert className="bg-primary/5 border-primary/20">
          <Sparkles className="h-4 w-4 text-primary" />
          <AlertDescription className="text-primary">
            AI suggestions applied! Review and modify as needed.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Organization Name - Required in review */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="reviewName">
            Organization Name <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="reviewName"
              placeholder="Your organization name"
              value={reviewName}
              onChange={(e) => setReviewName(e.target.value)}
              className="pl-10"
              required
              disabled={isSubmitting}
            />
          </div>
          {analysisResult?.suggested_name && (
            <p className="text-sm text-muted-foreground">
              Auto-detected from your website
            </p>
          )}
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="industry">Industry & Company Activities</Label>
          <TagInput
            id="industry"
            placeholder="e.g., B2B, fertilizer trading, agriculture, manufacturing"
            value={industry}
            onChange={setIndustry}
            disabled={isSubmitting}
            variant="info"
          />
          <p className="text-sm text-muted-foreground">
            Tags describing your industry and activities help us find relevant business partners.
          </p>
        </div>

        {/* Prospecting Goals - New Field */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="prospectingGoals">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              Prospecting Goals
            </div>
          </Label>
          <TagInput
            id="prospectingGoals"
            placeholder="e.g., new buyers, agricultural distributors, enterprise customers"
            value={prospectingGoals}
            onChange={setProspectingGoals}
            disabled={isSubmitting}
            variant="success"
          />
          <p className="text-sm text-muted-foreground">
            What types of leads, customers, or partners are you looking for? This helps us find the right data sources.
          </p>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="targetMarkets">Target Markets</Label>
          <MultiSelect
            id="targetMarkets"
            options={TARGET_MARKET_OPTIONS}
            value={targetMarkets}
            onChange={setTargetMarkets}
            placeholder="Select target markets"
            searchPlaceholder="Search markets..."
            disabled={isSubmitting}
          />
          <p className="text-sm text-muted-foreground">
            What types of customers do you sell to?
          </p>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="targetCountries">Target Countries</Label>
          <CountryRegionSelect
            id="targetCountries"
            value={targetCountries}
            onChange={setTargetCountries}
            placeholder="Select countries or regions"
            disabled={isSubmitting}
          />
          <p className="text-sm text-muted-foreground">
            Select countries or regions where your target customers are located
          </p>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting || !reviewName.trim()} className="min-w-[180px]">
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Organization'
          )}
        </Button>
      </div>
    </form>
  )
}
